// AllInOneHub — "7 apps in 1" orbital hub.
//
// Big central Calofay badge radiating curved SVG arrows out to seven
// feature chips (calorie / steps / water / workouts / recipes /
// notes-and-goals / before-after photos). Pure CSS + framer-motion;
// no external deps. Mounts under the Hero subtitle to anchor the
// value prop visually before the bullet list.
//
// Layout:
//   • Mobile  — center + 2-column grid of chips underneath (orbital
//     geometry doesn't survive narrow viewports).
//   • Tablet+ — true orbital layout; chips positioned via polar
//     trigonometry around the central circle, connectors drawn by a
//     full-cover SVG that recalculates endpoints on resize.

import { motion } from "framer-motion";
import { Flame, Footprints, Droplet, Dumbbell, ChefHat, Target, Camera, Sparkles, Bot } from "lucide-react";
import { useLandingLang } from "@/pages/LandingV2";

const APPS = [
  { icon: Flame,      tint: "from-orange-400 to-rose-400",     ar: "تطبيق حساب السعرات",   en: "Calorie tracker app" },
  { icon: Footprints, tint: "from-sky-400 to-indigo-400",      ar: "تطبيق عدّاد الخطوات",  en: "Step counter app" },
  { icon: Droplet,    tint: "from-cyan-400 to-blue-400",       ar: "تطبيق تسجيل الماء",     en: "Water logger app" },
  { icon: Dumbbell,   tint: "from-fuchsia-400 to-purple-500",  ar: "تطبيق حساب حرق التمارين",  en: "Workout burn tracker app" },
  { icon: ChefHat,    tint: "from-emerald-400 to-teal-400",    ar: "تطبيق وصفات صحية",     en: "Healthy recipes app" },
  { icon: Bot,        tint: "from-violet-500 to-indigo-500",   ar: "تطبيق شات ذكاء اصطناعي",  en: "AI chat app" },
  { icon: Target,     tint: "from-amber-400 to-yellow-500",    ar: "تطبيق كتابة ملاحظاتك وأهدافك",  en: "Write your notes & goals app" },
  { icon: Camera,     tint: "from-pink-400 to-rose-500",       ar: "تطبيق يخليك تشوف تقدمك وتقارن صورك قبل وبعد", en: "App that lets you see your progress and compare before/after photos" },
];

export default function AllInOneHub({ lang: langProp, isRTL: isRTLProp, compact = false }) {
  // Hub originally shipped inside LandingV2 so it reads `lang`/`isRTL`
  // from the landing-page context. When reused outside the landing
  // (e.g. the native paywall), callers pass `lang` + `isRTL` as props
  // and the context fallback is irrelevant.
  const ctx = useLandingLang();
  const lang = langProp ?? ctx.lang;
  const isRTL = isRTLProp ?? ctx.isRTL;
  const en = lang === "en";
  const N = APPS.length;
  // Chip positions on the circle — start at the top (-90°) and sweep
  // clockwise. Radius picks itself from the parent via % — the chips
  // stay anchored even if we resize the container later. We also tag
  // each position's half so the label can be rendered radially
  // OUTWARD from the center (top-half → label above icon, bottom-half
  // → label below). This keeps the visual mass balanced around the
  // medallion so it feels dead-centered. Radius is intentionally
  // small (33% of container) so chip + wrapped label always sit
  // inside the aspect-square, even on 360px-wide phones.
  const R = 33;
  const positions = APPS.map((_, i) => {
    const angle = (2 * Math.PI * i) / N - Math.PI / 2;
    const top = 50 + R * Math.sin(angle);
    return {
      left: 50 + R * Math.cos(angle), // percentages
      top,
      isTop: top < 50,
    };
  });

  return (
    <section
      className={compact ? "relative px-0 pt-0 pb-0" : "relative px-4 pt-2 pb-0"}
      dir={isRTL ? "rtl" : "ltr"}
      data-testid="lv2-all-in-one-hub"
    >
      <div className={compact ? "max-w-md mx-auto" : "max-w-5xl mx-auto"}>
        {/* ─── Section header — hidden in compact mode (the host
            screen already has its own title). ─── */}
        {!compact && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-6 md:mb-8"
        >
          <div className="inline-flex items-center gap-1.5 bg-brand-50 border border-brand-200/80 text-brand-700 text-[11px] md:text-xs font-black px-3 py-1 rounded-full mb-3">
            <Sparkles className="w-3 h-3" strokeWidth={3} />
            {en ? "All-in-one" : "كل شي في واحد"}
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-[34px] font-black text-neutral-900 leading-tight">
            {en ? (
              <>
                One Calofay subscription <span className="text-brand-500">replaces 8 apps</span>
              </>
            ) : (
              <>
                اشتراك واحد في كالوفاي <span className="text-brand-500">يغنيك عن 8 تطبيقات</span>
              </>
            )}
          </h2>
          <p className="text-sm md:text-[15px] text-neutral-500 mt-2 max-w-xl mx-auto leading-relaxed">
            {en
              ? "Everything you'd normally juggle in separate apps — now orbits one home."
              : "كل شي كنت تحتاجه بتطبيقات متفرقة — صار يدور حول تطبيق واحد."}
          </p>
        </motion.div>
        )}

        {/* ─── Orbital hub (all viewports) ─── */}
        <div className={`relative mx-auto aspect-square w-full ${compact ? "max-w-[320px]" : "max-w-[560px]"}`}>
          {/* Decorative orbit ring — matches the chip orbit radius so
              chips look like they're truly riding the orbit path.
              Thicker stroke + stronger color than before so it reads
              as an unmistakable orbit, not a faint decoration. */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div
              aria-hidden
              className="absolute rounded-full border-2 border-dashed border-brand-300/80"
              style={{
                width: `${2 * R}%`,
                height: `${2 * R}%`,
                animation: `hub-spin 45s linear infinite`,
              }}
            />
          </div>

          {/* SVG connectors — one curved line per app. Lines now start
              from the medallion EDGE (not its center) and end just at
              the chip EDGE (inner side) so each arrow visibly leaves
              the medallion and lands on the chip instead of piercing
              through it. Each line also has a small arrowhead for
              extra directionality. `preserveAspectRatio="none"` is
              fine because our container is a perfect square. */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden
          >
            <defs>
              <linearGradient id="hubLine" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#f97316" stopOpacity="0.95" />
                <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.65" />
              </linearGradient>
              {/* Arrowhead — pure fill, no stroke, solid orange so it
                  reads clearly at any angle. We previously applied a
                  Gaussian-blur glow filter to each path which was
                  silently clipping one wing of the arrowhead on
                  diagonal connectors (SVG filter regions are
                  per-element and can miss the marker's bounding box).
                  Dropping the filter — plus bumping the marker to 5×5
                  — makes every arrowhead render fully on every chip. */}
              <marker
                id="hubArrow"
                viewBox="-1 -1 12 12"
                refX="5"
                refY="5"
                markerWidth="5"
                markerHeight="5"
                orient="auto-start-reverse"
                markerUnits="userSpaceOnUse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#f97316" />
              </marker>
            </defs>
            {positions.map((p, i) => {
              // Direction from center to chip (unit vector).
              const dx = p.left - 50;
              const dy = p.top - 50;
              const len = Math.sqrt(dx * dx + dy * dy) || 1;
              const ux = dx / len;
              const uy = dy / len;
              // Trim both ends so the line leaves the medallion edge
              // and stops BEFORE the chip's icon tile. Chip wrappers
              // are taller than they look (icon + label stacked), and
              // the label-above-vs-below flip for top/bottom chips
              // shifts the icon off the geometric chip position by a
              // few percentage points. We use a larger `endOffset`
              // so the arrowhead always lands in the open gap between
              // the medallion and the chip, never under the icon.
              const startOffset = 13;
              const endOffset = 12;
              const sx = 50 + ux * startOffset;
              const sy = 50 + uy * startOffset;
              const ex = p.left - ux * endOffset;
              const ey = p.top - uy * endOffset;
              // Quadratic control point perpendicular to the line so
              // arrows fan out instead of being straight-radial.
              const mx = (sx + ex) / 2;
              const my = (sy + ey) / 2;
              const nx = -uy;
              const ny = ux;
              const curve = 4;
              const qx = mx + nx * curve;
              const qy = my + ny * curve;
              return (
                <motion.path
                  key={i}
                  d={`M ${sx} ${sy} Q ${qx} ${qy} ${ex} ${ey}`}
                  stroke="url(#hubLine)"
                  strokeWidth="0.7"
                  fill="none"
                  strokeLinecap="round"
                  markerEnd="url(#hubArrow)"
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 1 }}
                  viewport={{ once: true, amount: 0.4 }}
                  // Sequential reveal — chips land first (~1s), then
                  // each arrow draws ONE SECOND after the previous.
                  // Creates a storytelling cadence where each app
                  // gets its own moment to arrive.
                  transition={{
                    duration: 0.6,
                    delay: 1 + i * 1,
                    ease: "easeOut",
                  }}
                />
              );
            })}
          </svg>

          {/* ─── Center medallion ─── */}
          {/* The static wrapper handles positioning (left/top/translate)
              because framer-motion writes its own `transform` on the
              motion element, which would otherwise clobber our
              `-translate-x-1/2 -translate-y-1/2`. The inner motion.div
              only animates scale + opacity. */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ type: "spring", damping: 15, stiffness: 180, delay: 0.1 }}
            >
            <div className="relative">
              {/* Pulsing halo — tightened so it hugs the medallion
                  instead of bleeding into the surrounding chips. */}
              <div
                aria-hidden
                className="absolute inset-0 rounded-full orange-gradient opacity-35 blur-xl"
                style={{ animation: "hub-pulse 3s ease-in-out infinite" }}
              />
              <div className="relative w-20 h-20 sm:w-28 sm:h-28 md:w-36 md:h-36 rounded-full orange-gradient text-white shadow-[0_30px_60px_-20px_rgba(249,115,22,0.65)] flex flex-col items-center justify-center border-4 border-white/80">
                <div className="text-3xl sm:text-5xl md:text-6xl font-black leading-none num tracking-tight">8</div>
                <div className="text-[8px] sm:text-[10px] md:text-xs font-black uppercase tracking-widest mt-0.5 opacity-90">
                  {en ? "apps in 1" : "في 1"}
                </div>
                <div className="text-[7px] sm:text-[9px] md:text-[10px] font-bold opacity-80 mt-0.5">
                  {en ? "Calofay" : "كالوفاي"}
                </div>
              </div>
            </div>
            </motion.div>
          </div>

          {/* ─── Orbital chips ─── */}
          {APPS.map((app, i) => {
            const Icon = app.icon;
            const p = positions[i];
            return (
              <div
                key={app.ar}
                className="absolute -translate-x-1/2 -translate-y-1/2 z-20"
                style={{ left: `${p.left}%`, top: `${p.top}%` }}
              >
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  // Chips land first (within ~1s total) so the user
                  // sees all the destinations BEFORE the arrows start
                  // drawing one-by-one. Smaller stagger keeps the
                  // entrance snappy.
                  transition={{ type: "spring", damping: 18, stiffness: 220, delay: 0.3 + i * 0.08 }}
                  whileHover={{ scale: 1.08, y: -3 }}
                  className="group"
                  data-testid={`hub-chip-${i}`}
                >
                <div className={`flex ${p.isTop ? "flex-col-reverse" : "flex-col"} items-center gap-1`}>
                  <div
                    className={`relative w-9 h-9 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br ${app.tint} text-white flex items-center justify-center shadow-[0_12px_30px_-8px_rgba(0,0,0,0.25)] rotate-[-6deg] group-hover:rotate-0 transition-transform duration-300 border-[3px] border-white`}
                  >
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" strokeWidth={2.2} />
                    {/* Mini glow on hover */}
                    <div className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity bg-white/15" />
                  </div>
                  <div className="bg-white/95 backdrop-blur-sm text-neutral-800 text-[9px] sm:text-[10px] md:text-[11.5px] font-black px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-xl shadow-sm border border-neutral-200/80 text-center leading-tight max-w-[82px] sm:max-w-[110px] md:max-w-[140px]">
                    {en ? app.en : app.ar}
                  </div>
                </div>
                </motion.div>
              </div>
            );
          })}
        </div>

        {/* Mobile grid fallback intentionally removed — the orbital
            layout is now the single source of truth across every
            viewport, with chip + label sizes tapering down on narrow
            screens so the geometry still breathes. */}
      </div>

      {/* Keyframes — scoped as a style tag so we don't need to touch
          the global Tailwind config for a one-off orbit animation. */}
      <style>{`
        @keyframes hub-spin { to { transform: rotate(360deg); } }
        @keyframes hub-pulse { 0%,100% { transform: scale(1); opacity: 0.4; } 50% { transform: scale(1.08); opacity: 0.6; } }
        @keyframes hub-twinkle { 0%,100% { opacity: 0.4; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1.2); } }
      `}</style>
    </section>
  );
}
